import { XMarkIcon } from '@heroicons/react/24/solid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FighterManagerLinkResponseDto, Profile } from '../../types/types';

interface ManagerRequestPopupProps {
    showPopup: boolean;
    links: FighterManagerLinkResponseDto[];
    userProfile: Profile | null;
    actionLoading: number | null;
    onLinkAction: (linkId: number, approved: boolean) => Promise<void>;
    onClosePopup: () => void;
}

export const ManagerRequestPopup: React.FC<ManagerRequestPopupProps> = ({
    showPopup,
    links,
    userProfile,
    actionLoading,
    onLinkAction,
    onClosePopup,
}) => {
    const { t } = useTranslation('dashboard');

    if (!showPopup || !Array.isArray(links) || links.length === 0 || userProfile?.profileType !== 'FIGHTER') {
        return null;
    }

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-labelledby="popup-title"
        >
            <div className="bg-white shadow-lg max-w-md w-full rounded-md">
                <div className="bg-white px-2 py-2 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 id="popup-title" className="text-lg font-semibold text-gray-800">
                            {t('manager_request')}
                        </h2>
                        <button onClick={onClosePopup} className="p-1">
                            <XMarkIcon className="w-5 h-5 text-gray-600" />
                        </button>
                    </div>
                </div>

                <div className="p-4">
                    <div className="space-y-4">
                        {links.map((link, index) => (
                            <div
                                key={link.id}
                                className={`${index !== links.length - 1 ? 'border-b border-gray-200 pb-4' : ''}`}
                            >
                                <div className="flex items-center space-x-3">
                                    <img
                                        src={link.profile?.profileImage || '/default-avatar.png'}
                                        alt={`${link.profile?.firstName} ${link.profile?.lastName}`}
                                        className="w-10 h-10 object-cover rounded-full"
                                    />
                                    <span className="font-medium text-start text-gray-900">
                                        {link.profile?.firstName} {link.profile?.lastName}{' '}
                                        <span className="font-normal text-gray-500">
                                            {t('manager_request_content')}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end space-x-2 mt-6">
                        <button
                            onClick={() => onLinkAction(links[0].id, true)}
                            disabled={actionLoading === links[0].id}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                        >
                            {actionLoading === links[0].id ? '...' : t('accept')}
                        </button>
                        <button
                            onClick={() => onLinkAction(links[0].id, false)}
                            disabled={actionLoading === links[0].id}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                        >
                            {actionLoading === links[0].id ? '...' : t('decline')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};